import {
  fetchAllInteractionComments,
  createComment,
  deleteComment,
} from "@/api/comments";
import { useToast } from "vue-toastification";

// Use a string key (instead of Symbol) so provide/inject works even if the module
// is duplicated across chunks during bundling/HMR.
export const INTERNAL_COMMENTS_KEY = "INTERNAL_COMMENTS";

export const useInternalComments = (thread) => {
  const toast = useToast();
  const comments = ref([]);
  const tempComment = ref("");
  const isLoading = ref(false);
  const error = ref(null);
  const hasLoaded = ref(false);

  const resolvedThread = computed(() => unref(thread));
  const lastInteraction = computed(() =>
    resolvedThread.value?.interactions?.at(-1),
  );
  const lastInteractionId = computed(
    () => lastInteraction.value?.id ?? lastInteraction.value?._id ?? null,
  );

  const count = computed(() => comments.value.length);

  const refresh = async () => {
    const interactionId = lastInteractionId.value;
    if (!interactionId) {
      comments.value = [];
      hasLoaded.value = true;
      return;
    }

    isLoading.value = true;
    error.value = null;
    try {
      const data = await fetchAllInteractionComments(interactionId);
      comments.value = data || [];
      hasLoaded.value = true;
    } catch (e) {
      error.value = e;
      comments.value = [];
      hasLoaded.value = true;
    } finally {
      isLoading.value = false;
    }
  };

  const createInternalComment = async ({
    comment_text,
    tagged_users = [],
    interaction_id,
    organization_id,
  }) => {
    const resolved = resolvedThread.value;
    const payload = {
      interaction_id: interaction_id ?? lastInteractionId.value,
      organization_id: organization_id ?? resolved?.organization_id,
      comment_text,
      tagged_users,
    };

    isLoading.value = true;
    error.value = null;
    try {
      const res = await createComment(payload);
      const created = res?.data;
      if (created) {
        const normalized_comment = {
          ...created,
          _id: created.id ?? created._id,
        };
        comments.value.unshift(normalized_comment);
      }
      hasLoaded.value = true;
      return;
    } catch (e) {
      error.value = e;
      console.error("Error in createInternalComment. Error is: ", e);
      toast.error("Failed to create comment");
      throw e;
    } finally {
      isLoading.value = false;
    }
  };

  const deleteInternalComment = async (commentId) => {
    isLoading.value = true;
    error.value = null;
    try {
      const res = await deleteComment(commentId);
      if (res?.success === true) {
        comments.value = comments.value.filter((c) => c?._id !== commentId);
        hasLoaded.value = true;
        return;
      }
      
      // Refresh if there is no success key in response object
      await refresh();
      return;
    } catch (e) {
      error.value = e;
      console.error("Error in deleteInternalComment. Error is: ", e);
      toast.error("Failed to delete comment");
      throw e;
    } finally {
      isLoading.value = false;
    }
  };

  watch(lastInteractionId, refresh, { immediate: true });

  return {
    comments,
    tempComment,
    count,
    lastInteraction,
    lastInteractionId,
    isLoading,
    error,
    hasLoaded,
    refresh,
    createInternalComment,
    deleteInternalComment,
  };
};

